import { useState } from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import { Button, type ButtonProps } from '@mui/material';
import { styled } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import { getRecordingsExportUrl } from '../../requests/getRecordingsExportUrl';
import { ensureUserUuid } from '../../utils/ensureUserUuid';
import { createAndClickDownloadUrl } from '../../views/AdminSettings/utils/CreateAndClickDownladUrl';
import Loadable from '../common/loadable';

const StyledLoadable = styled(Loadable)`
    &[aria-busy='true'] {
        padding-right: 20px;
        .loading-overlay {
            right: -10px;
            align-items: end;
        }

        .MuiCircularProgress-root {
            width: 16px !important;
            height: 16px !important;
            margin-left: 8px;
            color: white;
        }
    }
`;

export const Exporter = ({ variant = 'contained', ...props }: Omit<ButtonProps, 'onClick'>) => {
    const { t } = useTranslation('admin_settings');
    const { data: schoolInfo } = useSchoolInfo();
    const { enqueueSnackbar } = useSnackbar();

    const userUuid = ensureUserUuid();
    const schoolUuid = schoolInfo?.uuid;
    const [downloading, setDownloading] = useState(false);

    const handleClickExport = async () => {
        if (!schoolUuid) {
            return;
        }
        setDownloading(true);

        let data;
        try {
            data = await getRecordingsExportUrl(userUuid);
        } catch (e) {
            console.error(e);
            enqueueSnackbar(t('recordings.exportRecordingsFailed'), { variant: 'error' });
            return;
        }

        if (data?.inProgress) {
            window.setTimeout(handleClickExport, 5000);
            return;
        }

        setDownloading(false);

        if (data?.url) {
            createAndClickDownloadUrl(data.url, t('recordings.exportRecordings'));
        }
    };

    if (!schoolUuid) {
        return null;
    }

    return (
        <Button variant={variant} {...props} onClick={handleClickExport} startIcon={<DownloadIcon />}>
            <StyledLoadable isLoading={downloading}>{t('recordings.exportRecordings')}</StyledLoadable>
        </Button>
    );
};
